import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Lock, Unlock, Search, X, Mail, Phone, MapPin,
  Clock, Plane, HeartPulse, ShieldCheck, Coffee, ChevronRight,
} from "lucide-react";
const ROLES = ["employee", "manager", "admin", "super_admin"];
const POSITIONS = ["Apotheker/in", "PTA", "PKA", "Botenfahrer", "Reinigungskraft", "Student", "Praktikant", "Filialleiter"];
const DAYS = [["mo", "Mo"], ["di", "Di"], ["mi", "Mi"], ["do", "Do"], ["fr", "Fr"], ["sa", "Sa"], ["so", "So"]];
const TRACKING_MODES = [
  { id: "both", label: "Beides (Web + Kiosk)" },
  { id: "kiosk_only", label: "Nur Kiosk-Terminal" },
  { id: "web_only", label: "Nur Web-Portal" },
];

const emptyUser = {
  username: "", password: "", first_name: "", last_name: "", email: "",
  role: "employee", position: "PTA", pin: "", location_ids: [], primary_location_id: null,
  weekly_hours: 40, work_hours: { mo: 8, di: 8, mi: 8, do: 8, fr: 8, sa: 0, so: 0 },
  time_buffer_minutes: 10, overtime_buffer_minutes: 10, late_buffer_minutes: 5, vacation_days_per_year: 30, absence_mode: "days",
  time_tracking_mode: "both",
  academic_title: "", birth_name: "", birthday: "", phone: "", mobile: "",
  street: "", house_number: "", zip_code: "", city: "",
  tax_number: "", personnel_number: "",
  contract_start: "", contract_end: "", gross_salary_monthly: 0, salary_count: 12, contract_note: "",
};

const roleBadge = (role) => {
  const map = {
    super_admin: "bg-violet-100 text-violet-800",
    admin: "bg-indigo-100 text-indigo-800",
    manager: "bg-blue-100 text-blue-800",
    employee: "bg-slate-100 text-slate-800",
    kiosk: "bg-amber-100 text-amber-800",
  };
  return map[role] || "bg-slate-100 text-slate-800";
};

export default function UsersPage() {
  const { branding } = useBranding();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dialog, setDialog] = useState(null); // 'create' | 'edit'
  const [form, setForm] = useState(emptyUser);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" ? window.innerWidth >= 1280 : true);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1280);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/users"), api.get("/locations")]);
    setUsers(r1.data);
    setLocations(r2.data);
  };
  useEffect(() => { load(); }, []);

  // Support ?edit=<userid> deep-link (from UserDetailSheet's default onEdit)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && users.length > 0) {
      const u = users.find(x => x.id === editId);
      if (u) {
        setForm({ ...u, password: "" });
        setDialog("edit");
        // clear the query param so refresh/back doesn't reopen
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [users, searchParams]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter === "active" && !u.active) return false;
      if (statusFilter === "inactive" && u.active) return false;
      if (!q) return true;
      const hay = `${u.first_name} ${u.last_name} ${u.username} ${u.email || ""} ${u.position || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search, roleFilter, statusFilter]);

  const locName = (id) => locations.find(l => l.id === id)?.name || "—";

  const openCreate = () => {
    setForm({ 
      ...emptyUser, 
      overtime_buffer_minutes: branding?.overtime_buffer_minutes ?? 10,
      late_buffer_minutes: branding?.late_buffer_minutes ?? 5,
      absence_mode: branding?.absence_mode_global ?? "days",
      primary_location_id: locations[0]?.id, 
      location_ids: locations[0] ? [locations[0].id] : [] 
    });
    setDialog("create");
  };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setDialog("edit"); };

  const openDetail = async (u) => {
    setSelectedId(u.id);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/personnel/user/${u.id}/summary`);
      setDetail(data);
    } catch (err) {
      toast.error("Details konnten nicht geladen werden");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const save = async () => {
    try {
      if (dialog === "create") { await api.post("/users", form); toast.success("Mitarbeiter angelegt"); }
      else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${form.id}`, payload);
        toast.success("Aktualisiert");
        // refresh detail if open
        if (selectedId === form.id) openDetail({ id: form.id });
      }
      setDialog(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Gelöscht"); if (selectedId === id) { setSelectedId(null); setDetail(null); } await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const toggleActive = async (u) => {
    const action = u.active ? "sperren" : "entsperren";
    if (!window.confirm(`Mitarbeiter wirklich ${action}?`)) return;
    try { await api.post(`/users/${u.id}/toggle-active`); toast.success(u.active ? "Gesperrt" : "Entsperrt"); await load(); if (selectedId === u.id) openDetail(u); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const closeDetail = () => { setSelectedId(null); setDetail(null); };

  return (
    <div className="space-y-6" data-testid="users-root">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="overline">Verwaltung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Mitarbeiter</h2>
          <p className="text-sm text-slate-500 mt-1">Zeile klicken für Detail-Ansicht ohne Seitenwechsel.</p>
        </div>
        <Button data-testid="users-add-btn" onClick={openCreate} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Mitarbeiter hinzufügen
        </Button>
      </div>

      {/* Filter bar */}
      <Card className="border-border/60">
        <CardContent className="p-3 flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="users-search"
              placeholder="Name, Benutzer, E-Mail, Position…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-44" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Rollen</SelectItem>
              {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-40" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="inactive">Gesperrt</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 md:ml-auto">{filtered.length} von {users.length}</div>
        </CardContent>
      </Card>

      {/* Split-pane layout: list left (desktop) + detail right; on mobile detail becomes sheet */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(360px,460px)] gap-4">
        <Card className="border-border/60">
          <CardContent className="p-0">
            <div className="divide-y divide-border/60">
              {filtered.map((u) => {
                const active = selectedId === u.id;
                return (
                  <button
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    onClick={() => openDetail(u)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${active ? "bg-blue-50/70 dark:bg-blue-950/30 border-l-4 border-blue-600" : "hover:bg-slate-50 dark:hover:bg-slate-900/40 border-l-4 border-transparent"}`}
                  >
                    <Avatar className="w-10 h-10 shrink-0">
                      {u.avatar_b64 && <AvatarImage src={u.avatar_b64} />}
                      <AvatarFallback className="bg-blue-100 text-blue-700 font-bold text-sm">
                        {(u.first_name?.[0] || "?") + (u.last_name?.[0] || "")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{u.first_name} {u.last_name}</span>
                        <Badge className={`${roleBadge(u.role)} text-[10px]`}>{u.role.replace("_", " ")}</Badge>
                        {!u.active && <Badge variant="secondary" className="text-[10px]">Gesperrt</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {u.position || "—"} · {u.username} · {u.weekly_hours}h/W
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">Kein Mitarbeiter passt zum Filter</div>}
            </div>
          </CardContent>
        </Card>

        {/* Desktop-only inline detail pane */}
        <div className="hidden xl:block">
          {detail ? (
            <DetailPane
              detail={detail}
              locations={locations}
              locName={locName}
              onEdit={() => openEdit(detail.user)}
              onToggle={() => toggleActive(detail.user)}
              onDelete={() => remove(detail.user.id)}
              onClose={closeDetail}
              loading={detailLoading}
            />
          ) : (
            <Card className="border-border/60 h-full">
              <CardContent className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]">
                <Search className="w-8 h-8 text-slate-300 mb-2" />
                <div className="font-medium">Wähle einen Mitarbeiter</div>
                <div className="text-xs mt-1">Alle Details erscheinen hier – kein Seitenwechsel nötig.</div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile / Tablet Sheet (hidden on xl where inline detail pane is used) */}
      <Sheet open={!!selectedId && !!detail && !isDesktop} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0 xl:hidden">
          <SheetHeader className="p-4 border-b border-border/60">
            <SheetTitle>Mitarbeiter-Details</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="p-4">
              <DetailPane
                detail={detail}
                locations={locations}
                locName={locName}
                onEdit={() => openEdit(detail.user)}
                onToggle={() => toggleActive(detail.user)}
                onDelete={() => remove(detail.user.id)}
                onClose={closeDetail}
                loading={detailLoading}
                embedded
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create/Edit dialog with tabs */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog === "create" ? "Mitarbeiter anlegen" : "Mitarbeiter bearbeiten"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="basis">
            <TabsList className="grid grid-cols-4 mb-3">
              <TabsTrigger value="basis" data-testid="edit-tab-basis">Basis</TabsTrigger>
              <TabsTrigger value="stamm" data-testid="edit-tab-stamm">Stammdaten</TabsTrigger>
              <TabsTrigger value="vertrag" data-testid="edit-tab-vertrag">Vertrag</TabsTrigger>
              <TabsTrigger value="soll" data-testid="edit-tab-soll">Sollzeit</TabsTrigger>
            </TabsList>
            <TabsContent value="basis" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vorname</Label><Input data-testid="user-form-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                <div><Label>Nachname</Label><Input data-testid="user-form-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                <div><Label>Benutzername</Label><Input data-testid="user-form-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} disabled={dialog === "edit"} /></div>
                <div><Label>E-Mail</Label><Input data-testid="user-form-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div>
                  <Label>{dialog === "create" ? "Passwort" : "Neues Passwort (leer = unverändert)"}</Label>
                  <Input data-testid="user-form-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div>
                  <Label>PIN (4-6 Ziffern)</Label>
                  <Input data-testid="user-form-pin" value={form.pin || ""} maxLength={6} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} />
                </div>
                <div>
                  <Label>Rolle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Position</Label>
                  <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                    <SelectTrigger data-testid="user-form-position"><SelectValue /></SelectTrigger>
                    <SelectContent>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hauptstandort</Label>
                  <Select value={form.primary_location_id || ""} onValueChange={(v) => setForm({ ...form, primary_location_id: v, location_ids: Array.from(new Set([...(form.location_ids || []), v])) })}>
                    <SelectTrigger data-testid="user-form-loc"><SelectValue placeholder="Standort" /></SelectTrigger>
                    <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Zeiterfassung (Erlaubnis)</Label>
                  <Select value={form.time_tracking_mode || "both"} onValueChange={(v) => setForm({ ...form, time_tracking_mode: v })}>
                    <SelectTrigger data-testid="user-form-tracking"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRACKING_MODES.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {dialog === "edit" && (
                  <div className="col-span-2 flex items-center gap-3">
                    <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                    <span className="text-sm">Aktiv</span>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="stamm" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Akademischer Titel</Label><Input data-testid="user-form-title" value={form.academic_title || ""} onChange={(e) => setForm({ ...form, academic_title: e.target.value })} placeholder="z. B. Dr., Prof." /></div>
                <div><Label>Geburtsname</Label><Input data-testid="user-form-birthname" value={form.birth_name || ""} onChange={(e) => setForm({ ...form, birth_name: e.target.value })} /></div>
                <div><Label>Geburtstag</Label><Input data-testid="user-form-birthday" type="date" value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} /></div>
                <div><Label>Personalnummer</Label><Input data-testid="user-form-persnr" value={form.personnel_number || ""} onChange={(e) => setForm({ ...form, personnel_number: e.target.value })} /></div>
                <div><Label>Telefon (Festnetz)</Label><Input data-testid="user-form-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Mobil</Label><Input data-testid="user-form-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
                <div className="col-span-2 grid grid-cols-[1fr_auto] gap-3">
                  <div><Label>Straße</Label><Input value={form.street || ""} onChange={(e) => setForm({ ...form, street: e.target.value })} /></div>
                  <div><Label>Nr.</Label><Input className="w-24" value={form.house_number || ""} onChange={(e) => setForm({ ...form, house_number: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-3">
                  <div><Label>PLZ</Label><Input className="w-24" value={form.zip_code || ""} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
                  <div><Label>Ort</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                </div>
                <div><Label>Steuernummer</Label><Input data-testid="user-form-tax" value={form.tax_number || ""} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="vertrag" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vertragsbeginn</Label><Input data-testid="user-form-cstart" type="date" value={form.contract_start || ""} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} /></div>
                <div><Label>Vertragsende (optional)</Label><Input data-testid="user-form-cend" type="date" value={form.contract_end || ""} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} /></div>
                <div><Label>Wochenstunden</Label><Input data-testid="user-form-weekly" type="number" step="0.5" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: parseFloat(e.target.value) })} /></div>
                <div><Label>Urlaubstage/Jahr</Label><Input data-testid="user-form-vacation" type="number" step="0.5" value={form.vacation_days_per_year} onChange={(e) => setForm({ ...form, vacation_days_per_year: parseFloat(e.target.value) })} /></div>
                <div><Label>Bruttogehalt monatlich (€)</Label><Input data-testid="user-form-gross" type="number" step="0.01" value={form.gross_salary_monthly || 0} onChange={(e) => setForm({ ...form, gross_salary_monthly: parseFloat(e.target.value || 0) })} /></div>
                <div><Label>Anzahl Gehälter/Jahr</Label><Input data-testid="user-form-salcount" type="number" min="1" max="14" value={form.salary_count || 12} onChange={(e) => setForm({ ...form, salary_count: parseInt(e.target.value || 12) })} /></div>
                <div><Label>Puffer für Überstunden (Min.)</Label><Input data-testid="user-form-ot-buffer" type="number" value={form.overtime_buffer_minutes ?? 10} onChange={(e) => setForm({ ...form, overtime_buffer_minutes: parseInt(e.target.value || 0) })} /></div>
                <div><Label>Puffer für Verspätungen (Min.)</Label><Input data-testid="user-form-late-buffer" type="number" value={form.late_buffer_minutes ?? 5} onChange={(e) => setForm({ ...form, late_buffer_minutes: parseInt(e.target.value || 0) })} /></div>
                <div>
                  <Label>Urlaubs-Abrechnungsmodus</Label>
                  <Select value={form.absence_mode} onValueChange={(v) => setForm({ ...form, absence_mode: v })}>
                    <SelectTrigger data-testid="user-form-mode"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="days">Tage</SelectItem><SelectItem value="hours">Stunden</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Bemerkung / Vertragsnotiz</Label>
                  <Textarea rows={3} value={form.contract_note || ""} onChange={(e) => setForm({ ...form, contract_note: e.target.value })} placeholder="z. B. (27 Wochenstunden × 36 Urlaubstage) / 6 Werktage = 162 Urlaubsstunden" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="soll">
              <Label className="mb-2 block">Soll-Stunden pro Wochentag</Label>
              <div className="grid grid-cols-7 gap-2">
                {DAYS.map(([key, label]) => (
                  <div key={key} className="text-center">
                    <div className="text-xs font-bold text-slate-500 mb-1">{label}</div>
                    <Input
                      data-testid={`user-form-day-${key}`}
                      type="number"
                      step="0.5"
                      className="text-center px-1"
                      value={form.work_hours?.[key] ?? 0}
                      onChange={(e) => setForm({ ...form, work_hours: { ...form.work_hours, [key]: parseFloat(e.target.value || 0) } })}
                    />
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Summe: {DAYS.reduce((s, [k]) => s + (parseFloat(form.work_hours?.[k]) || 0), 0).toFixed(1)}h
                {form.weekly_hours ? ` von ${form.weekly_hours}h Wochenstunden` : ""}
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button data-testid="user-form-save" onClick={save}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailPane({ detail, locations, locName, onEdit, onToggle, onDelete, onClose, loading, embedded }) {
  const u = detail.user;
  const s = detail.stats;
  return (
    <Card className={embedded ? "border-0 shadow-none" : "border-border/60 sticky top-24"} data-testid="user-detail-pane">
      <CardContent className={embedded ? "p-0" : "p-5"}>
        <div className="flex items-start gap-4">
          <Avatar className="w-16 h-16">
            {u.avatar_b64 && <AvatarImage src={u.avatar_b64} />}
            <AvatarFallback className="bg-blue-100 text-blue-700 font-bold text-lg">
              {(u.first_name?.[0] || "?") + (u.last_name?.[0] || "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg leading-tight">{u.first_name} {u.last_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{u.position || "—"} · {u.username}</div>
            <div className="flex flex-wrap gap-1 mt-2">
              <Badge className={roleBadge(u.role)}>{u.role.replace("_", " ")}</Badge>
              {u.active ? <Badge className="bg-emerald-100 text-emerald-800">Aktiv</Badge> : <Badge variant="secondary">Gesperrt</Badge>}
              <Badge variant="outline">{u.time_tracking_mode}</Badge>
            </div>
          </div>
          {!embedded && (
            <button onClick={onClose} data-testid="user-detail-close" className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className="rounded-lg border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Diesen Monat</div>
            <div className="font-heading font-bold text-lg mt-0.5">{s.month_hours.toFixed(1)}h</div>
            {s.month_overtime > 0 && <div className="text-xs text-amber-700">+{s.month_overtime.toFixed(1)}h Überstunden</div>}
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Plane className="w-3 h-3" /> Urlaub {new Date().getFullYear()}</div>
            <div className="font-heading font-bold text-lg mt-0.5">{s.vacation_days_year} / {u.vacation_days_per_year}</div>
            <div className="text-xs text-slate-500">Rest: {s.vacation_remaining}</div>
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><HeartPulse className="w-3 h-3" /> Kranktage</div>
            <div className="font-heading font-bold text-lg mt-0.5">{s.sick_days_year}</div>
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Wochensoll</div>
            <div className="font-heading font-bold text-lg mt-0.5">{u.weekly_hours}h</div>
            <div className="text-[10px] text-slate-500">Puffer ÜS: {u.overtime_buffer_minutes ?? 10}m · V: {u.late_buffer_minutes ?? 5}m</div>
          </div>
        </div>

        <Tabs defaultValue="info" className="mt-5">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="info" data-testid="detail-tab-info">Info</TabsTrigger>
            <TabsTrigger value="vertrag" data-testid="detail-tab-vertrag">Vertrag</TabsTrigger>
            <TabsTrigger value="hours" data-testid="detail-tab-hours">Soll</TabsTrigger>
            <TabsTrigger value="entries" data-testid="detail-tab-entries">Zeiten</TabsTrigger>
            <TabsTrigger value="absences" data-testid="detail-tab-absences">Urlaub</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="pt-3 space-y-2 text-sm">
            {u.academic_title && <InfoRow icon={ShieldCheck} label="Titel" value={u.academic_title} />}
            {u.birthday && <InfoRow icon={Plane} label="Geburtstag" value={new Date(u.birthday).toLocaleDateString("de-DE")} />}
            <InfoRow icon={Mail} label="E-Mail" value={u.email || "—"} />
            {u.phone && <InfoRow icon={Phone} label="Telefon" value={u.phone} />}
            {u.mobile && <InfoRow icon={Phone} label="Mobil" value={u.mobile} />}
            {(u.street || u.city) && <InfoRow icon={MapPin} label="Adresse" value={`${u.street || ""} ${u.house_number || ""}, ${u.zip_code || ""} ${u.city || ""}`.trim()} />}
            <InfoRow icon={MapPin} label="Hauptstandort" value={locName(u.primary_location_id)} />
            <InfoRow icon={MapPin} label="Weitere Standorte" value={(u.location_ids || []).filter(id => id !== u.primary_location_id).map(locName).join(", ") || "—"} />
            {u.personnel_number && <InfoRow icon={ShieldCheck} label="Personalnummer" value={u.personnel_number} />}
          </TabsContent>
          <TabsContent value="vertrag" className="pt-3 space-y-2 text-sm">
            <InfoRow icon={Clock} label="Vertragsbeginn" value={u.contract_start ? new Date(u.contract_start).toLocaleDateString("de-DE") : "—"} />
            {u.contract_end && <InfoRow icon={Clock} label="Vertragsende" value={new Date(u.contract_end).toLocaleDateString("de-DE")} />}
            <InfoRow icon={Clock} label="Wochenstunden" value={`${u.weekly_hours}h`} />
            <InfoRow icon={Plane} label="Urlaubstage/Jahr" value={`${u.vacation_days_per_year}`} />
            {u.gross_salary_monthly > 0 && (
              <InfoRow icon={ShieldCheck} label="Bruttogehalt" value={`${u.gross_salary_monthly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} × ${u.salary_count}/Jahr`} />
            )}
            {u.contract_note && (
              <div className="rounded-md border border-border/60 p-2 text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                {u.contract_note}
              </div>
            )}
          </TabsContent>
          <TabsContent value="hours" className="pt-3">
            <div className="grid grid-cols-7 gap-1">
              {[["mo","Mo"],["di","Di"],["mi","Mi"],["do","Do"],["fr","Fr"],["sa","Sa"],["so","So"]].map(([k, lbl]) => (
                <div key={k} className="rounded-md border border-border/60 py-2 text-center">
                  <div className="text-[10px] font-bold text-slate-500">{lbl}</div>
                  <div className="font-heading font-bold text-sm mt-0.5">{u.work_hours?.[k] ?? 0}h</div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="entries" className="pt-3">
            <div className="max-h-[280px] overflow-y-auto divide-y divide-border/40">
              {detail.entries.length === 0 && <div className="text-sm text-slate-500 text-center py-4">Keine Einträge</div>}
              {detail.entries.map(e => (
                <div key={e.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{new Date(e.date).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}</div>
                    <div className="text-xs text-slate-500">{e.clock_in?.slice(11,16)} – {e.clock_out?.slice(11,16) || "…"} · Pause {e.break_minutes || 0}m</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold">{((e.net_minutes || 0) / 60).toFixed(1)}h</div>
                    {e.overtime_minutes > 0 && <div className="text-[10px] text-amber-700">+{(e.overtime_minutes/60).toFixed(1)}h Ü.</div>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="absences" className="pt-3">
            <div className="max-h-[280px] overflow-y-auto divide-y divide-border/40">
              {detail.absences.length === 0 && <div className="text-sm text-slate-500 text-center py-4">Keine Abwesenheiten</div>}
              {detail.absences.map(a => (
                <div key={a.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium capitalize">{a.type}</div>
                    <div className="text-xs text-slate-500">{a.start_date} – {a.end_date} · {a.days || 0}T</div>
                    {a.reason && <div className="text-xs text-slate-400 italic">{a.reason}</div>}
                  </div>
                  <Badge variant={a.status === "approved" ? "default" : "secondary"} className={a.status === "approved" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}>{a.status}</Badge>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-border/60">
          <Button data-testid="detail-edit-btn" size="sm" onClick={onEdit}><Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten</Button>
          <Button data-testid="detail-toggle-btn" size="sm" variant="outline" onClick={onToggle}>
            {u.active ? <><Lock className="w-3.5 h-3.5 mr-1 text-amber-600" /> Sperren</> : <><Unlock className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Entsperren</>}
          </Button>
          <Button data-testid="detail-delete-btn" size="sm" variant="ghost" onClick={onDelete} className="text-rose-600 hover:text-rose-700 ml-auto">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
          </Button>
        </div>
        {loading && <div className="text-xs text-slate-400 mt-2 text-center">Aktualisiere…</div>}
      </CardContent>
    </Card>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm truncate">{value}</div>
      </div>
    </div>
  );
}
